import { useMemo, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth, type UserRole } from '../contexts/AuthContext';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { useViewStore } from '@/lib/viewStore';
import { Plus, Shield, ShieldCheck, UserX, Pencil } from 'lucide-react';

type User = {
    id: string;
    account_id: string;
    email: string;
    name: string;
    role: UserRole;
    is_active: boolean;
    last_login_at: string | null;
    created_at: string;
    updated_at: string;
    invite_token?: string;
    invited_at?: string;
    accepted_at?: string;
};

type UserFormState = {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    role: UserRole;
};

type FormMode = 'direct' | 'invite'; // direct = immediate password, invite = send invite link

const defaultFormState = (): UserFormState => ({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin',
});

import { validatePasswordStrength } from '@littlebooker/dal';

// User CRUD goes through the authenticated API (which forwards the per-account
// JWT so RLS scopes it) — NOT direct Supabase, which RLS denies. The API hashes
// passwords server-side, so plaintext is sent over the already-TLS'd API call and
// never touches PostgREST from the browser.

async function fetchUsers(_accountId: string): Promise<User[]> {
    return apiGet('/api/v1/users');
}

async function createUser(_accountId: string, data: { email: string; name: string; password: string; role: UserRole }): Promise<User> {
    return apiPost('/api/v1/users', {
        email: data.email,
        name: data.name,
        password: data.password,
        role: data.role,
    });
}

async function inviteUser(accountId: string, currentUserId: string, data: { email: string; name: string; role: UserRole }): Promise<{ user: User; inviteLink: string }> {
    // Always use API endpoint for invite creation (secure backend operation)
    return await apiPost('/api/v1/users/invite', {
        email: data.email,
        name: data.name,
        role: data.role,
    });
}

async function updateUser(userId: string, data: { name?: string; role?: UserRole; is_active?: boolean; password?: string }): Promise<User> {
    return apiPatch(`/api/v1/users/${userId}`, data);
}

export default function UsersPage() {
    const queryClient = useQueryClient();
    const { account, user: currentUser, canCreateUsers } = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formMode, setFormMode] = useState<FormMode>('direct');
    const [formState, setFormState] = useState<UserFormState>(() => defaultFormState());
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [inviteLink, setInviteLink] = useState<string | null>(null);
    const viewMode = useViewStore((state) => state.getViewMode('users'));
    const setViewMode = useViewStore((state) => state.setViewMode);

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users', account?.id],
        queryFn: () => account ? fetchUsers(account.id) : Promise.resolve([]),
        enabled: !!account?.id,
    });

    // Filter users based on search query
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return users;
        const lowerQuery = searchQuery.toLowerCase();
        return users.filter(
            (user) =>
                user.name.toLowerCase().includes(lowerQuery) ||
                user.email.toLowerCase().includes(lowerQuery) ||
                user.role.toLowerCase().includes(lowerQuery)
        );
    }, [users, searchQuery]);

    const createMutation = useMutation({
        mutationFn: async (data: { email: string; name: string; password?: string; role: UserRole; mode: FormMode }) => {
            if (!account || !currentUser) throw new Error('No account or user');
            if (data.mode === 'direct') {
                if (!data.password) throw new Error('Password required for direct creation');
                const user = await createUser(account.id, { ...data, password: data.password });
                return { user, inviteLink: null };
            } else {
                return await inviteUser(account.id, currentUser.id, data);
            }
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            if (formMode === 'invite' && result.inviteLink) {
                // Show invite link
                setInviteLink(result.inviteLink);
            } else {
                closeDialog();
            }
        },
        onError: (error: Error) => {
            setFormError(error.message);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: { name?: string; role?: UserRole; is_active?: boolean; password?: string } }) =>
            updateUser(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            closeDialog();
        },
        onError: (error: Error) => {
            setFormError(error.message);
        },
    });

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const isOwner = canCreateUsers();

    const startCreateFlow = () => {
        if (!isOwner) return;
        setEditingUser(null);
        setFormMode('direct');
        setFormState(defaultFormState());
        setFormError(null);
        setInviteLink(null);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setInviteLink(null);
        setTimeout(() => {
            setEditingUser(null);
            setFormMode('direct');
            setFormState(defaultFormState());
            setFormError(null);
        }, 200);
    };

    const handleFieldChange = (field: keyof UserFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormState((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
        setFormError(null);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);

        if (!formState.name.trim()) {
            setFormError('Name is required');
            return;
        }

        if (!formState.email.trim()) {
            setFormError('Email is required');
            return;
        }

        // For direct mode, require password
        if (!editingUser && formMode === 'direct' && !formState.password) {
            setFormError('Password is required');
            return;
        }

        if (formState.password && formState.password !== formState.confirmPassword) {
            setFormError('Passwords do not match');
            return;
        }

        if (formState.password) {
            const strengthError = validatePasswordStrength(formState.password);
            if (strengthError) {
                setFormError(strengthError);
                return;
            }
        }

        if (editingUser) {
            const updateData: { name?: string; role?: UserRole; password?: string } = {
                name: formState.name.trim(),
                role: formState.role,
            };
            if (formState.password) {
                updateData.password = formState.password;
            }
            updateMutation.mutate({ id: editingUser.id, data: updateData });
        } else {
            createMutation.mutate({
                email: formState.email.trim(),
                name: formState.name.trim(),
                password: formState.password,
                role: formState.role,
                mode: formMode,
            });
        }
    };

    const openEditFlow = (user: User) => {
        if (!isOwner) return;
        setEditingUser(user);
        setFormState({
            name: user.name,
            email: user.email,
            password: '',
            confirmPassword: '',
            role: user.role,
        });
        setFormError(null);
        
        // If user has a pending invite, show the invite link
        if (user.invite_token && !user.accepted_at) {
            const baseUrl = import.meta.env.VITE_ADMIN_BASE_URL || window.location.origin;
            setInviteLink(`${baseUrl}/accept-invite/${user.invite_token}`);
        } else {
            setInviteLink(null);
        }
        
        setDialogOpen(true);
    };

    const handleToggleActive = async (user: User) => {
        if (!isOwner) return;
        // Don't allow deactivating yourself
        if (user.id === currentUser?.id) return;
        
        await updateMutation.mutateAsync({
            id: user.id,
            data: { is_active: !user.is_active },
        });
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-sm uppercase tracking-wide text-muted-foreground">Account Settings</p>
                    <h1 className="text-3xl font-bold">Users</h1>
                    <p className="text-muted-foreground">
                        Manage user accounts for your organization. {!isOwner && 'Only owners can create and edit users.'}
                    </p>
                </div>
                {isOwner && (
                    <Button onClick={startCreateFlow}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add User
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-4">
                <SearchFilter
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search users by name, email, or role..."
                    className="w-full max-w-sm"
                />
                <ViewToggle
                    view={viewMode}
                    onViewChange={(mode) => setViewMode('users', mode)}
                />
                {searchQuery && (
                    <span className="text-sm text-muted-foreground">
                        {filteredUsers.length} of {users.length} users
                    </span>
                )}
            </div>

            {isLoading ? (
                <p>Loading...</p>
            ) : users.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold">No users yet</h3>
                        <p className="text-muted-foreground mt-2 max-w-sm">
                            {isOwner 
                                ? 'Create your first user to start managing access to your account.'
                                : 'Contact your account owner to add users.'
                            }
                        </p>
                        {isOwner && (
                            <Button className="mt-4" onClick={startCreateFlow}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add First User
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : viewMode === 'card' ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredUsers.map((user) => (
                        <Card
                            key={user.id}
                            role={isOwner ? 'button' : undefined}
                            tabIndex={isOwner ? 0 : undefined}
                            onClick={() => isOwner && openEditFlow(user)}
                            onKeyDown={(event) => {
                                if (isOwner && (event.key === 'Enter' || event.key === ' ')) {
                                    event.preventDefault();
                                    openEditFlow(user);
                                }
                            }}
                            className={`border-muted transition ${
                                isOwner ? 'cursor-pointer hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary' : ''
                            } ${!user.is_active ? 'opacity-60' : ''}`}
                        >
                            <CardHeader className="space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <CardTitle className="text-xl flex items-center gap-2">
                                            {user.name}
                                            {user.id === currentUser?.id && (
                                                <span className="text-xs bg-blue-badge text-blue-badge-foreground px-2 py-0.5 rounded-full">You</span>
                                            )}
                                            {user.invite_token && !user.accepted_at && (
                                                <span className="text-xs bg-amber-badge text-amber-badge-foreground px-2 py-0.5 rounded-full">Pending Invite</span>
                                            )}
                                        </CardTitle>
                                        <CardDescription>{user.email}</CardDescription>
                                    </div>
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${
                                        user.role === 'owner'
                                            ? 'bg-purple-badge text-purple-badge-foreground'
                                            : 'bg-blue-badge text-blue-badge-foreground'
                                    }`}>
                                        {user.role === 'owner' ? (
                                            <ShieldCheck className="h-3 w-3" />
                                        ) : (
                                            <Shield className="h-3 w-3" />
                                        )}
                                        {user.role}
                                    </span>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="space-y-1 text-sm text-muted-foreground">
                                    <p>Last login: {formatDate(user.last_login_at)}</p>
                                    <p>Created: {formatDate(user.created_at)}</p>
                                </div>
                                {!user.is_active && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-badge text-red-badge-foreground px-2 py-0.5 text-xs">
                                        <UserX className="h-3 w-3" />
                                        Inactive
                                    </span>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last Login</TableHead>
                                {isOwner && <TableHead>Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.map((user) => (
                                <TableRow
                                    key={user.id}
                                    className={`${isOwner ? 'cursor-pointer' : ''} ${!user.is_active ? 'opacity-60' : ''}`}
                                    onClick={() => isOwner && openEditFlow(user)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-2">
                                            {user.name}
                                            {user.id === currentUser?.id && (
                                                <span className="text-xs bg-blue-badge text-blue-badge-foreground px-1.5 py-0.5 rounded">You</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {user.email}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${
                                            user.role === 'owner'
                                                ? 'bg-purple-badge text-purple-badge-foreground'
                                                : 'bg-blue-badge text-blue-badge-foreground'
                                        }`}>
                                            {user.role === 'owner' ? (
                                                <ShieldCheck className="h-3 w-3" />
                                            ) : (
                                                <Shield className="h-3 w-3" />
                                            )}
                                            {user.role}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {user.is_active ? (
                                            user.invite_token && !user.accepted_at ? (
                                                <span className="text-warning flex items-center gap-1">
                                                    <Shield className="h-3 w-3" />
                                                    Pending Invite
                                                </span>
                                            ) : (
                                                <span className="text-success">Active</span>
                                            )
                                        ) : (
                                            <span className="text-destructive flex items-center gap-1">
                                                <UserX className="h-3 w-3" />
                                                Inactive
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(user.last_login_at)}
                                    </TableCell>
                                    {isOwner && (
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditFlow(user);
                                                    }}
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </Button>
                                                {user.id !== currentUser?.id && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleActive(user);
                                                        }}
                                                    >
                                                        {user.is_active ? 'Deactivate' : 'Activate'}
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
                <DialogContent size="medium">
                    {inviteLink ? (
                        <div className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>
                                    {editingUser ? `Pending Invite: ${editingUser.name}` : 'Invite Link Created'}
                                </DialogTitle>
                                <DialogDescription>
                                    {editingUser 
                                        ? 'This user has a pending invite. Share this link with them to accept.'
                                        : 'Share this link with the user to accept their invitation.'
                                    }
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-3">
                                <div className="bg-info-muted border border-info-border rounded-md p-4 space-y-2">
                                    <label className="text-sm font-medium text-info-foreground">Invite Link</label>
                                    <div className="flex gap-2">
                                        <input
                                            readOnly
                                            value={inviteLink}
                                            className="flex-1 px-3 py-2 text-sm border border-info-border rounded-md bg-card"
                                            onClick={(e) => (e.target as HTMLInputElement).select()}
                                        />
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(inviteLink);
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <p className="text-xs text-info-foreground/80">
                                        This link can only be used once. The user will set their own password.
                                    </p>
                                </div>
                            </div>

                            <DialogFooter>
                                {editingUser && (
                                    <Button 
                                        type="button" 
                                        variant="outline"
                                        onClick={() => setInviteLink(null)}
                                    >
                                        Edit User
                                    </Button>
                                )}
                                <Button type="button" onClick={closeDialog}>
                                    Done
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>{editingUser ? `Edit ${editingUser.name}` : 'Add User'}</DialogTitle>
                                <DialogDescription>
                                    {editingUser 
                                        ? 'Update user details. Leave password blank to keep current password.'
                                        : formMode === 'invite'
                                            ? 'Send an invite link. The user will set their own password.'
                                            : 'Create a new user account with a password.'
                                    }
                                </DialogDescription>
                            </DialogHeader>

                            {!editingUser && (
                                <div className="flex gap-2 p-1 bg-muted rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormMode('direct');
                                            setFormError(null);
                                        }}
                                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition ${
                                            formMode === 'direct'
                                                ? 'bg-background shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        Direct Creation
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormMode('invite');
                                            setFormError(null);
                                            setFormState(prev => ({ ...prev, password: '', confirmPassword: '' }));
                                        }}
                                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition ${
                                            formMode === 'invite'
                                                ? 'bg-background shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        Send Invite Link
                                    </button>
                                </div>
                            )}

                            {formError && (
                            <div className="bg-error-muted border border-error-border text-error-foreground px-4 py-3 rounded-md text-sm">
                                {formError}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium">Name</label>
                                <input
                                    required
                                    value={formState.name}
                                    onChange={handleFieldChange('name')}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                    placeholder="John Doe"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium">Email</label>
                                <input
                                    required
                                    type="email"
                                    value={formState.email}
                                    onChange={handleFieldChange('email')}
                                    disabled={!!editingUser}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                                    placeholder="user@example.com"
                                />
                                {editingUser && (
                                    <p className="text-xs text-muted-foreground">Email cannot be changed after creation</p>
                                )}
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium">Role</label>
                                <select
                                    value={formState.role}
                                    onChange={handleFieldChange('role')}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                >
                                    <option value="admin">Admin</option>
                                    <option value="owner">Owner</option>
                                </select>
                                <p className="text-xs text-muted-foreground">
                                    Owners can create and manage users. Admins have all other permissions.
                                </p>
                            </div>

                            {(editingUser || formMode === 'direct') && (
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">
                                        {editingUser ? 'New Password (optional)' : 'Password'}
                                    </label>
                                    <input
                                        type="password"
                                        value={formState.password}
                                        onChange={handleFieldChange('password')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                                        required={!editingUser && formMode === 'direct'}
                                    />
                                </div>
                            )}

                            {(editingUser || formMode === 'direct') && (formState.password || !editingUser) && (
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={formState.confirmPassword}
                                        onChange={handleFieldChange('confirmPassword')}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                                        placeholder="Confirm password"
                                        required={!editingUser || !!formState.password}
                                    />
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSaving}>
                                {isSaving 
                                    ? 'Saving...' 
                                    : editingUser 
                                        ? 'Save Changes' 
                                        : formMode === 'invite'
                                            ? 'Create Invite'
                                            : 'Create User'
                                }
                            </Button>
                        </DialogFooter>
                    </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
